import {
    BaseElement,
    ElementOverrideDtoKeys,
    ElementOverrideDtoValues,
    EntityWithElements,
    OneOfCustomPropertyDtos,
    OneOfCustomPropertyOverrideDtos,
    OneOfElementDtos,
    OneOfElementOverrideDtos,
    OneOfVisualElementDtos
} from '@domain/api/design-api.interop';
import {
    CustomPropertyFeedDto,
    CustomPropertyTextDto,
    EllipseElementDto,
    EllipseElementOverrideDto,
    FilterDto,
    FilterOverrideDto,
    GroupNodeDto,
    GroupNodeOverrideDto,
    ImageElementDto,
    ImageElementOverrideDto,
    RectangleElementDto,
    RectangleElementOverrideDto,
    TextLikeElementDto,
    TextLikeElementOverrideDto,
    VideoElementDto,
    VideoElementOverrideDto,
    WidgetElementDto,
    WidgetElementOverrideDto
} from '@domain/api/generated/design-api';
import { ElementKind } from '@domain/elements';
import { OneOfDapiMaskableNodeDtos, OneOfDapiMaskableOverrideNodeDtos } from '@domain/mask';
import { OneOfDataNodes } from '@domain/nodes';
import { RequiredKeys } from '@domain/utils/types';
import { isObject } from '@studio/utils/utils';
import {
    GetOverrideValue,
    InferredElementOverride,
    MergableProperty,
    OverrideElementOptions,
    OverrideProperty,
    RemovableProperty,
    RemovablePropertyKeys,
    RemovedProperty
} from './design-api.types';

export function isRemovedPropertyObject(object: unknown): object is RemovedProperty {
    if (isObject(object) && 'isRemoved' in object) {
        return true;
    }

    return false;
}

export function isOverrideObject<T extends OverrideProperty>(
    object: unknown
): object is OverrideProperty<T> {
    if (isObject(object) && 'value' in object) {
        return true;
    }

    return false;
}

export function isRemovablePropertyKey(key: ElementOverrideDtoKeys): key is RemovablePropertyKeys {
    switch (key) {
        case 'border':
        case 'fill':
        case 'parentId':
        case 'parentNodeId':
        case 'ratio':
            return true;

        default:
            return false;
    }
}

export function getElementOverrideOfEntity<
    Element extends BaseElement,
    ReturnedElement extends Element extends OneOfElementDtos
        ? InferredElementOverride<Element>
        : OneOfElementOverrideDtos
>(poolElement: Element, entity?: EntityWithElements): ReturnedElement | undefined {
    return entity?.elements[poolElement.id] as ReturnedElement | undefined;
}

export function getOverrideOrValue<
    ElementValue,
    OverrideDtoValue extends ElementOverrideDtoValues | RemovableProperty
>(
    value: ElementValue,
    override: OverrideDtoValue | undefined = undefined,
    shouldOverride?: boolean
): ElementValue | GetOverrideValue<OverrideDtoValue> {
    if (typeof override === 'undefined') {
        return value;
    }

    if (isRemovedPropertyObject(override)) {
        return undefined as GetOverrideValue<OverrideDtoValue>;
    }

    const overrideValue = isOverrideObject(override) ? override.value : override;

    if (isObject(value) && isObject(overrideValue)) {
        if (shouldOverride) {
            return overrideValue as GetOverrideValue<OverrideDtoValue>;
        }

        // Merge if partial object override
        return { ...value, ...overrideValue };
    }

    return overrideValue as GetOverrideValue<OverrideDtoValue>;
}

export function isCompleteOverrideProperty(property: (string & {}) | MergableProperty): boolean {
    return (<MergableProperty>['imageSettings', 'masking']).includes(
        property as MergableProperty[number]
    );
}

function isDapiNodeDtoType(node: OneOfElementDtos, type: OneOfElementDtos['$type']): boolean {
    return Boolean(node.$type === type);
}

function isDapiNodeOverrideDtoType(
    node: OneOfElementOverrideDtos,
    type: OneOfElementOverrideDtos['$type']
): boolean {
    return Boolean(node.$type === type);
}

export function isDapiImageNodeDto(node: OneOfElementDtos): node is ImageElementDto {
    return isDapiNodeDtoType(node, 'ImageElementDto');
}
export function isDapiTextLikeNodeDto(node: OneOfElementDtos): node is TextLikeElementDto {
    return isDapiNodeDtoType(node, 'TextLikeElementDto');
}
export function isDapiVideoNodeDto(node: OneOfElementDtos): node is VideoElementDto {
    return isDapiNodeDtoType(node, 'VideoElementDto');
}
export function isDapiRectangleNodeDto(node: OneOfElementDtos): node is RectangleElementDto {
    return isDapiNodeDtoType(node, 'RectangleElementDto');
}
export function isDapiEllipseNodeDto(node: OneOfElementDtos): node is EllipseElementDto {
    return isDapiNodeDtoType(node, 'EllipseElementDto');
}
export function isDapiWidgetNodeDto(node: OneOfElementDtos): node is WidgetElementDto {
    return isDapiNodeDtoType(node, 'WidgetElementDto');
}
export function isDapiWidgetWithAssetNodeDto(
    node: OneOfElementDtos
): node is RequiredKeys<WidgetElementDto, 'widgetAsset'> {
    return isDapiWidgetNodeDto(node) && !!node.widgetAsset;
}
export function isDapiGroupNodeDto(node: OneOfElementDtos): node is GroupNodeDto {
    return isDapiNodeDtoType(node, 'GroupNodeDto');
}

export function isDapiGroupNodeOverrideDto(
    node: OneOfElementOverrideDtos
): node is GroupNodeOverrideDto {
    return isDapiNodeOverrideDtoType(node, 'GroupNodeOverrideDto');
}

export function isDapiWidgetElementOverrideDto(
    node: OneOfElementOverrideDtos
): node is WidgetElementOverrideDto {
    return isDapiNodeOverrideDtoType(node, 'WidgetElementOverrideDto');
}

export function isDapiTextLikeElementOverrideDto(
    node: OneOfElementOverrideDtos
): node is TextLikeElementOverrideDto {
    return isDapiNodeOverrideDtoType(node, 'TextLikeElementOverrideDto');
}

export function isDapiRectangleElementOverrideDto(
    node: OneOfElementOverrideDtos
): node is RectangleElementOverrideDto {
    return isDapiNodeOverrideDtoType(node, 'RectangleElementOverrideDto');
}

export function isDapiEllipseElementOverrideDto(
    node: OneOfElementOverrideDtos
): node is EllipseElementOverrideDto {
    return isDapiNodeOverrideDtoType(node, 'EllipseElementOverrideDto');
}

export function isDapiImageElementOverrideDto(
    node: OneOfElementOverrideDtos
): node is ImageElementOverrideDto {
    return isDapiNodeOverrideDtoType(node, 'ImageElementOverrideDto');
}

export function isDapiVideoElementOverrideDto(
    node: OneOfElementOverrideDtos
): node is VideoElementOverrideDto {
    return isDapiNodeOverrideDtoType(node, 'VideoElementOverrideDto');
}

function isCustomPropertyDtoType(
    customProperty: OneOfCustomPropertyDtos,
    type: OneOfCustomPropertyDtos['$type']
): boolean {
    return Boolean(customProperty.$type === type);
}

export function isCustomTextPropertyDto(
    customProperty: OneOfCustomPropertyDtos
): customProperty is CustomPropertyTextDto {
    return isCustomPropertyDtoType(customProperty, 'CustomPropertyTextDto');
}
export function isCustomFeedPropertyDto(
    customProperty: OneOfCustomPropertyDtos
): customProperty is CustomPropertyFeedDto {
    return isCustomPropertyDtoType(customProperty, 'CustomPropertyFeedDto');
}

export function isVersionableCustomPropertyDto(
    customProperty: OneOfCustomPropertyDtos
): customProperty is CustomPropertyTextDto | CustomPropertyFeedDto {
    return isCustomTextPropertyDto(customProperty) || isCustomFeedPropertyDto(customProperty);
}

export function isDapiMaskingSupportedDto(
    node: OneOfVisualElementDtos
): node is OneOfDapiMaskableNodeDtos {
    return (
        isDapiRectangleNodeDto(node) ||
        isDapiEllipseNodeDto(node) ||
        isDapiImageNodeDto(node) ||
        isDapiVideoNodeDto(node)
    );
}

export function isDapiMaskingOverrideSupportedDto(
    node: OneOfElementOverrideDtos
): node is OneOfDapiMaskableOverrideNodeDtos {
    return (
        isDapiRectangleElementOverrideDto(node) ||
        isDapiEllipseElementOverrideDto(node) ||
        isDapiImageElementOverrideDto(node) ||
        isDapiVideoElementOverrideDto(node)
    );
}

export function getKindFromDesignApiElement(
    element: OneOfElementDtos
): Exclude<
    ElementKind,
    ElementKind.BannerflowLibraryWidget | ElementKind.BannerflowLibraryWidgetInstance
> {
    switch (element.$type) {
        case 'WidgetElementDto':
            return ElementKind.Widget;

        case 'TextLikeElementDto':
            return element.isButton ? ElementKind.Button : ElementKind.Text;

        case 'EllipseElementDto':
            return ElementKind.Ellipse;

        case 'GroupNodeDto':
            return ElementKind.Group;

        case 'RectangleElementDto':
            return ElementKind.Rectangle;

        case 'ImageElementDto':
            return ElementKind.Image;

        case 'VideoElementDto':
            return ElementKind.Video;
    }
}

export function getWidgetCustomPropertiesOverride(
    poolElement: WidgetElementDto,
    { creativeElement, sizeElement, versionElement }: OverrideElementOptions<WidgetElementDto>
): WidgetElementOverrideDto['customProperties'] {
    const customProperties: WidgetElementOverrideDto['customProperties'] = {};

    for (const customProperty of poolElement.customProperties) {
        customProperties[customProperty.name] = {
            ...versionElement?.customProperties?.[customProperty.name],
            ...sizeElement?.customProperties?.[customProperty.name],
            ...creativeElement?.customProperties?.[customProperty.name]
        } as OneOfCustomPropertyOverrideDtos;
    }

    return customProperties;
}

export function getOverrideDtoType<
    ElementDto extends BaseElement,
    Type extends InferredElementOverride<ElementDto>['$type']
>(element: ElementDto): Type {
    switch (element.$type) {
        case 'RectangleElementDto':
            return 'RectangleElementOverrideDto' as Type;

        case 'EllipseElementDto':
            return 'EllipseElementOverrideDto' as Type;

        case 'ImageElementDto':
            return 'ImageElementOverrideDto' as Type;

        case 'TextLikeElementDto':
            return 'TextLikeElementOverrideDto' as Type;

        case 'WidgetElementDto':
            return 'WidgetElementOverrideDto' as Type;

        case 'VideoElementDto':
            return 'VideoElementOverrideDto' as Type;

        case 'GroupNodeDto':
            return 'GroupNodeOverrideDto' as Type;

        default:
            throw new Error('Could not get override Dto type');
    }
}

export function getDtoTypeFromNode(element: OneOfDataNodes): OneOfElementDtos['$type'] {
    switch (element.kind) {
        case ElementKind.Rectangle:
            return 'RectangleElementDto';

        case ElementKind.Ellipse:
            return 'EllipseElementDto';

        case ElementKind.Image:
            return 'ImageElementDto';

        case ElementKind.Text:
        case ElementKind.Button:
            return 'TextLikeElementDto';

        case ElementKind.Widget:
            return 'WidgetElementDto';

        case ElementKind.Video:
            return 'VideoElementDto';

        case ElementKind.Group:
            return 'GroupNodeDto';

        default:
            throw new Error('Could not get Dto type');
    }
}

export function getOverrideDtoTypeFromNode(element: OneOfDataNodes): OneOfElementOverrideDtos['$type'] {
    switch (element.kind) {
        case ElementKind.Rectangle:
            return 'RectangleElementOverrideDto';

        case ElementKind.Ellipse:
            return 'EllipseElementOverrideDto';

        case ElementKind.Image:
            return 'ImageElementOverrideDto';

        case ElementKind.Text:
        case ElementKind.Button:
            return 'TextLikeElementOverrideDto';

        case ElementKind.Widget:
            return 'WidgetElementOverrideDto';

        case ElementKind.Video:
            return 'VideoElementOverrideDto';

        case ElementKind.Group:
            return 'GroupNodeOverrideDto';

        default:
            throw new Error('Could not get override Dto type');
    }
}

export function convertFiltersToFiltersOverride(filters?: FilterDto): FilterOverrideDto | undefined {
    if (!filters) {
        return undefined;
    }

    return Object.entries(filters).reduce(
        (acc, [key, value]) => ({
            ...acc,
            [key]: { value } satisfies FilterOverrideDto[keyof FilterOverrideDto]
        }),
        {} as FilterOverrideDto
    );
}
